import React, { useState, useRef, useCallback, useEffect, memo } from "react";
import moment from "moment";

// Redux
import { useDispatch } from "react-redux";
import { setDataAlert } from "../../../../../../../@core/redux/actions/app/alert";
import { setStatusSpinner } from "../../../../../../../@core/redux/actions/app/spinner";

// Services (โปรเจคเก่าใช้ apiReqService)
import { apiServices } from "../../../../../../../@core/services/apiReqService";

// @Utility
import alertText from "../../../../../../../@utility/app/alertText"; // ดึง alertText มาใช้งาน

// Icons (MUI v4)
import CheckIcon from "@material-ui/icons/Check";
import UploadIcon from "@material-ui/icons/Publish";
import UploadFileIcon from '@material-ui/icons/AttachFile';

// Component (MUI v4)
import { Grid } from '@material-ui/core';

// CSS
import styles from "./../../../../assets/css/Style.module.css";
import buttonStyles from "./../../../../assets/css/Button.module.css";

// Img
import certifiedGif from "images/crypto_icon/icon-animation/Certified 1.gif";

const BoxSlipP2P = memo(({ p2pMode, money, depositItems, paymentResultState, setMode, timeDepositProcess, setTimeDepositProcess }) => {
  const dispatch = useDispatch();

  // ─── States สำหรับระบบนับเวลาและโหมดรีวิว ───
  const [isExpired, setIsExpired] = useState(false);
  const [timeShow, setTimeShow] = useState("15:00");
  const [urlImage, setUrlImage] = useState("");
  const intervalRef = useRef(null);

  // ─── States และ Refs สำหรับการอัปโหลดไฟล์ ───
  const fileInputRef = useRef(null);
  const [imageUpload, setImageUpload] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);

  const p2pResult = paymentResultState || depositItems?.payment_result || {};
  const currentLeg = p2pResult.legs?.[0] || {};
  const displayAmount = currentLeg.leg_amount || p2pResult.amount || money || 0;

  const isReviewMode = p2pMode === "slip-review" || !!depositItems?.slip;

  // ─── 1. แปลง Path เป็น URL เพื่อโชว์รูปสลิป (โหมดรีวิว) ───
  useEffect(() => {
    if (depositItems?.slip) {
      apiServices({ path: depositItems.slip }, "post", `/general/generate/link`).then((data) => {
        if (data?.code === 200 || data?.code === 0) {
          setUrlImage(data.result);
        }
      });
    }
  }, [depositItems?.slip]);

  // ─── 2. ระบบนับเวลาถอยหลัง (Timer Engine) ───
  const handleTimeout = useCallback(() => {
    dispatch(
      setDataAlert({
        type: "warning",
        title: "หมดเวลาทำรายการ",
        text: "คุณไม่ได้แนบสลิปภายในเวลาที่กำหนด กรุณาทำรายการใหม่อีกครั้ง",
        action: true,
        show: true,
        buttonText: "ตกลง",
      })
    );
    setMode("cancel");
    setTimeDepositProcess(null);
  }, [dispatch, setMode, setTimeDepositProcess]);

  const updateCountdown = useCallback(() => {
    if (isReviewMode) return;

    const deadline = currentLeg?.appointment_deadline || timeDepositProcess;
    if (!deadline) return;

    const targetTime = moment.utc(deadline).local();
    const now = moment();
    const duration = moment.duration(targetTime.diff(now));
    const seconds = duration.asSeconds();

    if (seconds <= 0) {
      clearInterval(intervalRef.current);
      if (!isExpired) {
        setIsExpired(true);
        setTimeShow("00:00");
        handleTimeout();
      }
    } else {
      const m = String(Math.floor(seconds / 60)).padStart(2, "0");
      const s = String(Math.ceil(seconds % 60)).padStart(2, "0");
      setTimeShow(`${m}:${s}`);
    }
  }, [isReviewMode, timeDepositProcess, currentLeg.appointment_deadline, isExpired, handleTimeout]);

  useEffect(() => {
    if (isReviewMode) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    updateCountdown();
    intervalRef.current = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalRef.current);
  }, [updateCountdown, isReviewMode]);

  // ─── 3. ฟังก์ชันจัดการไฟล์อัปโหลด ───
  const onImageChange = (e) => {
    let imageData = e.target.files;
    if (imageData.length > 0) {
      const file = imageData[0];
      if (file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg") {
        setImageUpload(file);
        setLocalPreview(URL.createObjectURL(file));
      } else {
        dispatch(setDataAlert({
          type: "warning",
          title: "แจ้งเตือน",
          text: "ไฟล์รูปภาพไม่ถูกต้อง กรุณาเลือกรูปภาพใหม่อีกครั้ง !",
          action: true,
          show: true,
          buttonText: "ตกลง"
        }));
        setImageUpload(null);
        setLocalPreview(null);
      }
    } else {
      setImageUpload(null);
      setLocalPreview(null);
    }
    
    // 📍 เคลียร์ค่า input เพื่อให้ผู้ใช้สามารถเลือกไฟล์เดิมซ้ำได้ในกรณีที่เปลี่ยนใจ
    e.target.value = null; 
  };

  const confirmUploadSlip = () => {
    if (!imageUpload) return;

    const dataUpload = new FormData();
    dataUpload.append('slip', imageUpload);
    if (depositItems?.time_create) dataUpload.append('td_date', depositItems.time_create);
    if (depositItems?.id) dataUpload.append('td_id', depositItems.id);

    dispatch(setStatusSpinner({ status: true, textProcess: "ระบบกำลังอัปโหลดสลิป กรุณารอสักครู่..." }));

    apiServices(dataUpload, "file", "/customer/payment-v2/deposit/slip").then((data) => {
      const { code: statusCode, message } = data;
      dispatch(setStatusSpinner({ status: false, textProcess: null }));

      if (statusCode === 200 || statusCode === 0) {
        dispatch(setDataAlert({
          type: "success",
          title: "แจ้งเตือน",
          text: "แนบรูปสลิปสำเร็จ !",
          action: true,
          redirect: null,
          show: true,
          buttonText: "ตกลง"
        }));

        // เคลียร์ภาพเพื่อให้ Polling จับสถานะใหม่และตัดเข้าโหมด Review
        setImageUpload(null);
        setLocalPreview(null);
      }
      else {
        setImageUpload(null);
        setLocalPreview(null);

        if (statusCode === 99) {
          dispatch(setDataAlert({
            type: "warning",
            title: "แจ้งเตือน",
            text: message,
            action: true,
            redirect: null,
            show: true,
            buttonText: "ตกลง"
          }));
        }
        else {
          let snackbarJson = alertText(statusCode);
          dispatch(setDataAlert({
            type: snackbarJson.statusAlert,
            title: snackbarJson.titleAlert,
            text: snackbarJson.textAlert,
            action: true,
            redirect: null,
            show: true,
            buttonText: "ตกลง"
          }));
        }
      }
    }).catch((err) => {
      dispatch(setStatusSpinner({ status: false, textProcess: null }));
      console.error(err);
    });
  };

  return (
    <div className="boxPaddingBottom50">

      {/* ─── Stepper ─── */}
      <div className={styles.boxMarginFormTop}>
        <div className={styles.boxDetailTop}>
          <div className="p2pStepperWrapper">
            <div className="p2pStepItem">
              <div className="p2pStepCircle p2pStepCircleChecked"><CheckIcon style={{ fontSize: 14 }} /></div>
              <div className="p2pStepLabel p2pStepLabelActive">จับคู่บัญชี</div>
            </div>
            <div className="p2pStepConnector p2pStepConnectorActive" />
            <div className="p2pStepItem">
              <div className="p2pStepCircle p2pStepCircleChecked"><CheckIcon style={{ fontSize: 14 }} /></div>
              <div className="p2pStepLabel p2pStepLabelActive">โอนเงิน</div>
            </div>
            <div className="p2pStepConnector p2pStepConnectorActive" />
            <div className="p2pStepItem">
              <div className="p2pStepCircle p2pStepCircleActive"><span className="p2pStepDot" /></div>
              <div className="p2pStepLabel p2pStepLabelActive">แนบสลิป</div>
            </div>
          </div>
        </div>
      </div>

      {isReviewMode ? (
        <>
          {/* ─── โหมดรีวิวสลิป (ตรวจสอบ) ─── */}
          <div className={styles.boxMarginFormTop}>
            <div className={styles.boxDetailTop}>
              <div className="center"><img className="p2pMatchingGif" src={certifiedGif} alt="กำลังตรวจสอบสลิป" /></div>
              <div className={["center", styles.boxMarginTop].join(" ")}>
                <span className="p2pMatchingText">ระบบกำลังตรวจสอบ กรุณารอสักครู่...</span>
              </div>
            </div>
          </div>

          <div className={styles.boxMarginFormTop}>
            <div className={styles.boxDetailTop}>
              <div className={styles.boxAccountAgentTitle}>แนบสลิปการโอนเงิน</div>
              <div className={styles.boxMarginTop}>
                {urlImage ? (
                  <div className="center">
                    <img src={urlImage} alt="สลิปที่แนบ" className="p2pSlipPreviewImg borderRadius8" />
                  </div>
                ) : (
                  <div className="center p2pSlipEmptyText">กำลังโหลดรูปสลิป...</div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* ─── โหมดอัปโหลดสลิป ─── */}
          <div className={styles.boxMarginFormTop03}>
            <div className={styles.boxDetailTop}>
              <div className="center boxPaddingBottom02">
                <div className="boxDetailAmountQrcodeNumber center marginTop0" style={{ marginTop: "0px" }}>
                  กรุณาทำรายการภายใน : <span className="boxDetailAmountQrcodeNumberText">{timeShow === "expired" ? "หมดเวลา" : timeShow ? timeShow : "00:00"}</span>
                </div>
              </div>

              <div className={styles.boxMarginFormTop03}>
                <div className={[styles.boxDetailTop, "bgTertiary"].join(" ")}>
                  <div className={styles.boxAccountAgentTitle}>กรุณาแนบสลิปการโอนเงิน</div>
                  <div className={["bankNameResponsive", "textMarginTop", "color-text-secondary"].join(" ")}>
                    หลังโอนเงินสำเร็จ กรุณาแนบสลิปการโอนเงิน เพื่อยืนยันรายการ และดำเนินการเติมเครดิต <br /><br />
                    ยอดโอน: <span className="color-primary">฿{Number(displayAmount).toLocaleString()}</span><br />
                    อัปโหลดสลิป รองรับไฟล์ JPG, PNG, ขนาดไม่เกิน 5MB
                  </div>
                </div>

                {/* 📍 พื้นที่อัปโหลด/พรีวิวรูปภาพ */}
                <div className="contentMarginTop03" onClick={() => !isExpired && fileInputRef.current && fileInputRef.current.click()}>
                  {localPreview ? (
                    <div className="center">
                      <img
                        src={localPreview}
                        alt="preview"
                        className="imageSlipPreview cursorPointer borderRadius8"
                      />
                    </div>
                  ) : (
                    <div className={["depositSlip", isExpired ? "opacityLow" : ""].join(" ")}>
                      <Grid className="cursorPointer marginLeft10" container alignItems="center" justifyContent="center" spacing={1}>
                        <Grid item>
                          <UploadFileIcon className="iconSlipImageUploadSize" />
                        </Grid>
                        <Grid item>
                          <div className="file-upload-wrapper02" data-text={"เลือกสลิป"}>
                            <input
                              ref={fileInputRef}
                              name="file-upload-field"
                              type="file"
                              className="file-upload-field displayNone"
                              onChange={onImageChange}
                              onClick={(e) => e.stopPropagation()} /* 📍 ป้องกัน Event Bubbling ไม่ให้เด้ง 2 รอบ */
                              accept="image/png, image/jpeg, image/jpg"
                            />
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </div>

              </div>

              {/* 📍 ปุ่มกดอัปโหลด จะโชว์เมื่อมีรูปและยังไม่หมดเวลา */}
              {(imageUpload && !isExpired) && (
                <div className={[styles.boxMarginFormTop03, "center"].join(" ")}>
                  <button
                    className={buttonStyles.btnConfirmSlip}
                    style={{
                      opacity: imageUpload ? "1" : "0.5",
                      cursor: imageUpload ? "pointer" : "no-drop"
                    }}
                    onClick={confirmUploadSlip}
                  >
                    <Grid container alignItems="center" justifyContent="center" spacing={2}>
                      <Grid item>
                        <UploadIcon className="iconTransactionaction" />
                      </Grid>
                      <Grid item>
                        อัพโหลดสลิป
                      </Grid>
                    </Grid>
                  </button>
                </div>
              )}

            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default BoxSlipP2P;